import React, { useEffect } from 'react';
import './Toast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  /** Whether the toast is visible */
  show: boolean;
  /** Type of toast (affects styling and icon) */
  type: ToastType;
  /** Toast message */
  message: string;
  /** Optional detailed description */
  description?: string;
  /** Duration in milliseconds (default: 5000) */
  duration?: number;
  /** Callback when toast should be hidden */
  onHide: () => void;
  /** Optional custom icon (overrides default type icon) */
  icon?: string;
  /** Position of the toast */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

export const Toast: React.FC<ToastProps> = ({
  show,
  type,
  message,
  description,
  duration = 5000,
  onHide,
  icon,
  position = 'top-right'
}) => {
  useEffect(() => {
    if (show && duration > 0) {
      const timer = setTimeout(() => {
        onHide();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [show, duration, onHide]);

  if (!show) return null;

  // Default icons for each type
  const defaultIcons: Record<ToastType, string> = {
    success: '✅',
    error: '❌',
    warning: '⚠️',
    info: 'ℹ️'
  };

  const displayIcon = icon || defaultIcons[type] || '';

  return (
    <div className={`toast toast--${type} toast--${position}`}>
      <div className="toast__icon">
        <span className="toast__icon-symbol">{displayIcon}</span>
      </div>
      <div className="toast__content">
        <div className="toast__message">{message}</div>
        {description && (
          <div className="toast__description">{description}</div>
        )}
      </div>
      <button 
        className="toast__close"
        onClick={onHide}
        aria-label="Close"
      >
        ✕
      </button>
    </div>
  );
};

// Convenience hook for easy usage
export const useToast = () => {
  const [toast, setToast] = React.useState<{
    show: boolean;
    type: ToastType;
    message: string;
    description?: string;
    duration?: number;
    icon?: string;
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  }>({
    show: false,
    type: 'info',
    message: '',
    duration: 5000,
    position: 'top-right'
  });

  const showToast = (config: {
    type: ToastType;
    message: string;
    description?: string;
    duration?: number;
    icon?: string;
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  }) => {
    setToast({
      show: true,
      ...config
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  return {
    toast: {
      ...toast,
      onHide: hideToast
    },
    showToast,
    hideToast
  };
};

export default Toast;
